import { useIntl } from 'react-intl';
import Button from '@rio-cloud/rio-uikit/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import useClipboard from '@rio-cloud/rio-uikit/useClipboard';

import { useGetDataMockFileQuery } from '@/services/dfpFrontendApi';
import type { DataMockFileMeta } from '../../types';
import CodeBlock from '../CodeBlock/CodeBlock';

interface FileViewerTarget {
    vin: string;
    file: DataMockFileMeta;
}

interface FileViewerModalProps {
    target: FileViewerTarget | null;
    onClose: () => void;
}

const FileViewerModal = ({ target, onClose }: FileViewerModalProps) => {
    const intl = useIntl();
    const isOpen = target !== null;
    const file = target?.file;

    const { data: content, isLoading } = useGetDataMockFileQuery(
        { vin: target?.vin ?? '', type: file?.type ?? '', category: file?.category ?? '' },
        { skip: !isOpen },
    );

    const { copy, copied } = useClipboard({ timeout: 2000 });

    if (!isOpen || !file) return null;

    const body = isLoading ? (
        <div className="display-flex justify-content-center padding-25">
            <Spinner />
        </div>
    ) : (
        <CodeBlock code={content ?? ''} language="yaml" />
    );

    const footer = (
        <Button variant="outline" onClick={() => copy(content ?? '')} disabled={isLoading}>
            <span className={`rioglyph ${copied ? 'rioglyph-ok' : 'rioglyph-duplicate'} margin-right-5`} />
            {copied
                ? intl.formatMessage({ id: 'intl-msg:common.copied' })
                : intl.formatMessage({ id: 'intl-msg:fileViewer.copyContent' })}
        </Button>
    );

    return (
        <Dialog
            show={isOpen}
            onClose={onClose}
            title={
                <span className="display-flex align-items-center gap-10">
                    <span className="rioglyph rioglyph-document text-color-primary" />
                    <span>
                        {file.name}
                        <span className="text-size-12 text-color-gray font-family-mono margin-left-10">
                            {file.path}
                        </span>
                    </span>
                </span>
            }
            body={body}
            footer={footer}
            bsSize="lg"
            showCloseButton
        />
    );
};

export default FileViewerModal;
